'use client';

/**
 * Captures, manages, and persists UTM and traffic attribution data across client-side navigation.
 *
 * - Extracts and stores UTMs, canonical tracking parameters, and traffic type from URL parameters and referrer.
 * - Updates or initializes stored attribution in storage and analytics without overwriting existing data when parameters are absent.
 * - Re-applies active tracking parameters to the URL on route changes.
 *
 * @returns {null} Runs purely as a side effect.
 */
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { CANONICAL_TRACKED_PARAMS_KEYS } from '@workspace/lib/constants/analytics';
import { saveAnalyticsValue } from '@workspace/lib/utils/analytics/analytics-local-storage';
import { checkForEqualParams } from '@workspace/lib/utils/analytics/check-for-equal-params';
import { extractParamsFromUrl } from '@workspace/lib/utils/analytics/extract-params-from-url';
import { persistTrackingParamsInUrl } from '@workspace/lib/utils/analytics/persist-tracking-params-in-url';
import {
  detectTrafficType,
  hasTrafficAttributionParams,
} from '@workspace/lib/utils/detect-traffic-type';
import {
  ActiveAttributionData,
  extractUtmParams,
  getStoredUtmParams,
  storeUtmParams,
  updateStoredUtmParamsPreserveExpiry,
  type UtmParams,
} from '@workspace/lib/utils/utm';

export interface UseUtmTrackingOutput {
  utmParams: UtmParams | null;
  hasUtm: boolean;
  clearUtm: () => void;
  isLoading?: boolean;
}

export function useUtmTracking() {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  useEffect(() => {
    const currentSearchParams = new URLSearchParams(window.location.search);
    const existingAttribution = getStoredUtmParams();

    let activeAttributionData: ActiveAttributionData;

    if (hasTrafficAttributionParams(currentSearchParams)) {
      const candidateTrafficType = detectTrafficType(
        currentSearchParams,
        document.referrer,
      );
      const urlUtms = extractUtmParams(currentSearchParams);
      const urlTrackingParams = extractParamsFromUrl(
        currentSearchParams,
        CANONICAL_TRACKED_PARAMS_KEYS,
      );

      activeAttributionData = {
        trafficType: candidateTrafficType,
        utms: urlUtms,
        trackingParams: urlTrackingParams,
      };

      const hasSameParams =
        Boolean(existingAttribution) &&
        checkForEqualParams(urlUtms, existingAttribution?.utms) &&
        checkForEqualParams(
          urlTrackingParams,
          existingAttribution?.trackingParams,
        );

      if (hasSameParams) {
        updateStoredUtmParamsPreserveExpiry(activeAttributionData);
      } else {
        storeUtmParams(activeAttributionData);
      }

      saveAnalyticsValue('trafficType', candidateTrafficType);
    } else if (!existingAttribution) {
      const candidateTrafficType = detectTrafficType(
        currentSearchParams,
        document.referrer,
      );

      activeAttributionData = {
        trafficType: candidateTrafficType,
        utms: {},
        trackingParams: {},
      };
      storeUtmParams(activeAttributionData);
      saveAnalyticsValue('trafficType', candidateTrafficType);
    } else {
      activeAttributionData = existingAttribution;
    }

    const mergedParams = {
      ...activeAttributionData.utms,
      ...activeAttributionData.trackingParams,
    };

    if (Object.keys(mergedParams).length > 0) {
      persistTrackingParamsInUrl({ searchParams, mergedParams, pathname });
    }
  }, [pathname, searchParams]);

  return null;
}
